import { ADMIN_STATS_DAYS, type AdminStatsDays } from "@listou/shared/schemas/admin";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { duration, time, useAdminDays } from "../components/admin/retro";
import { adminSystemQuery } from "../lib/queries";
import { viewerQuery } from "../lib/session";

const isPeriod = (value: unknown): value is AdminStatsDays =>
  ADMIN_STATS_DAYS.some((option) => option === Number(value));

export const Route = createFileRoute("/_authed/admin")({
  // The period lives in the URL, so it survives a tab change and a reload.
  validateSearch: (search: Record<string, unknown>): { j?: AdminStatsDays } =>
    isPeriod(search.j) ? { j: Number(search.j) as AdminStatsDays } : {},
  // The server answers 404 to anyone else; this only spares a non-admin the empty page.
  beforeLoad: async ({ context: { queryClient } }) => {
    const viewer = await queryClient.query({ ...viewerQuery, staleTime: "static" });
    if (!viewer.user.isAdmin) throw redirect({ to: "/mes-listes" });
  },
  component: AdminLayout,
});

const TABS = [
  { to: "/admin", label: "vue", key: "0" },
  { to: "/admin/systeme", label: "système", key: "1" },
  { to: "/admin/conso", label: "conso", key: "2" },
  { to: "/admin/croissance", label: "croissance", key: "3" },
  { to: "/admin/designs", label: "designs", key: "4" },
  { to: "/admin/sites", label: "sites", key: "5" },
  { to: "/admin/produits", label: "produits", key: "6" },
  { to: "/admin/listes", label: "listes", key: "7" },
] as const;

type Look = "auto" | "light" | "dark";
const LOOKS: Look[] = ["auto", "light", "dark"];
const LOOK_LABELS: Record<Look, string> = { auto: "auto", light: "clair", dark: "sombre" };
const LOOK_KEY = "listou.admin.look";

/** Per-browser preference; storage may be unavailable (private mode), auto then. */
const readLook = (): Look => {
  try {
    const stored = localStorage.getItem(LOOK_KEY);
    return LOOKS.find((look) => look === stored) ?? "auto";
  } catch {
    return "auto";
  }
};

function AdminLayout() {
  const days = useAdminDays();
  const [look, setLook] = useState<Look>("auto");
  useEffect(() => setLook(readLook()), []);
  const cycleLook = () => {
    const next = LOOKS[(LOOKS.indexOf(look) + 1) % LOOKS.length] ?? "auto";
    setLook(next);
    try {
      localStorage.setItem(LOOK_KEY, next);
    } catch {
      // Not remembered, still applied.
    }
  };
  const navigate = useNavigate();
  const system = useQuery(adminSystemQuery);

  // Number keys switch tabs, as on a terminal menu; never while typing in a field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target instanceof HTMLElement && event.target.closest("input, textarea, select")) {
        return;
      }
      const tab = TABS.find((candidate) => candidate.key === event.key);
      if (tab) void navigate({ to: tab.to, search: { j: days } });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [days, navigate]);

  return (
    <div
      className="retro min-h-dvh px-3 py-3 sm:px-4"
      data-retro={look === "auto" ? undefined : look}
    >
      <header
        className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-b pb-2"
        style={{ borderColor: "var(--r-line)" }}
      >
        <strong style={{ color: "var(--r-amber)" }}>LISTOU//ADMIN</strong>
        <nav className="flex flex-wrap gap-1">
          {TABS.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              search={{ j: days }}
              className="retro-btn"
              activeOptions={{ exact: tab.to === "/admin", includeSearch: false }}
              activeProps={{ "aria-pressed": true }}
            >
              <span style={{ opacity: 0.6 }}>{tab.key}</span> {tab.label}
            </Link>
          ))}
        </nav>
        {system.data && (
          <span style={{ color: "var(--r-dim)" }}>
            {system.data.environment} · rel {system.data.release?.slice(0, 7) ?? "dev"} · api up{" "}
            {duration(system.data.process.uptimeSeconds)} · lu à {time(system.data.checkedAt)}
            {system.isFetching ? " ↻" : ""}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <button type="button" className="retro-btn" onClick={cycleLook} title="Apparence">
            ◐ {LOOK_LABELS[look]}
          </button>
          <span style={{ color: "var(--r-dim)" }}>période</span>
          {ADMIN_STATS_DAYS.map((option) => (
            <button
              key={option}
              type="button"
              className="retro-btn"
              aria-pressed={days === option}
              onClick={() => navigate({ to: ".", search: { j: option } })}
            >
              {option}j
            </button>
          ))}
        </span>
      </header>
      <Outlet />
    </div>
  );
}
